<script setup lang="ts">
import { defineAsyncComponent, computed, ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead, useDevice } from '#imports'
import { SHOW_HR_BANNER_URL } from '~/constants'
import { EVENT, PAGE_NOT_FOUND, RESET_PASSWORD_URL } from '~/configs/page-url'
import { useLogin } from '~/composables/account/use-login'
import { useResetCookie } from '~/composables/common/use-reset-cookie'
import { useUserData } from '~/composables/account/use-user-data'
import { useIframe } from '~/composables/iframe/use-iframe'
import { useMaintain } from '~/composables/common/use-maintain'
import { useModalStore } from '~/stores/modal.store.ts'
import { useDepositData } from '~composables/deposit/use-deposit-data'
import { useSocket } from '~/composables/common/use-socket'
import { useSyncAuthenticateDataTab } from '~/composables/common/use-sync-authenticate-tab'
import { useLangdingPageLogin } from '~/composables/common/use-landing-page-login'
import { useRegisterAddon } from '~/composables/common/use-register-addon'
import { useLighthouse } from '~/composables/common/use-lighthouse'

const AppHeader = defineAsyncComponent(async () => await import('~/components/desktop/layouts/header.vue'))
const AppFooter = defineAsyncComponent(async () => await import('~/components/desktop/layouts/footer.vue'))
const BannerSlider = defineAsyncComponent(async () => await import('~/components/desktop/pages/home/hero-banner.vue'))
const ModalLoginSignUp = defineAsyncComponent(
  async () => await import('~/components/desktop/auth/modal-login-sign-up.vue')
)
const ModalForgotPassword = defineAsyncComponent(
  async () => await import('~/components/desktop/auth/modal-forgot-password.vue')
)
const ModalResetPassword = defineAsyncComponent(
  async () => await import('~/components/desktop/auth/modal-reset-password.vue')
)
const ModalUpdateDisplayName = defineAsyncComponent(
  async () => await import('~/components/desktop/auth/modal-update-diplayname.vue')
)
const Modal500k = defineAsyncComponent(async () => await import('~/components/common/modal-500k.vue'))
const FloatingEvent = defineAsyncComponent(async () => await import('~/components/common/event/floating.vue'))
const WelcomePopup = defineAsyncComponent(async () => await import('~/components/common/event/welcome-popup.vue'))
const LiveChat = defineAsyncComponent(async () => await import('~/components/common/live-chat.vue'))
const FloatingEventHalloween = defineAsyncComponent(async () => await import('~/components/common/event/halloween/floating.vue'))
const EventEntryHalloween = defineAsyncComponent(async () => await import('~/components/common/event/halloween/event-entry.vue'))

const { isLighthouseBot } = useLighthouse()
const store = useModalStore()
const { isLogged, openAuthModalByQuery } = useLogin()
const { isWindows, isMacOS, isChrome } = useDevice()
const { resetCookie } = useResetCookie()
const { setMaintenanceGames } = useMaintain()
const pageName = ref()
const { currentUser } = useUserData()
const { checkIsIframe } = useIframe()
const { initListAgency } = useDepositData()
const route = useRoute()
const {
  EVENT_PREMIER_LEAGUE_RANKING_ENABLED,
  EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED,
  EVENT_HALLOWEEN_ENABLE,
  EVENT_HALLOWEEN_POPUP_ENABLE
} = useRuntimeConfig().public

const isHiddenEvent = computed(() => {
  return route.path === PAGE_NOT_FOUND || route.path === EVENT.PREMIER_LEAGUE_RANKING
})

const isHiddenEventHalloween = computed(() => {
  return route.path === PAGE_NOT_FOUND || route.path === EVENT.HALLOWEEN
})

if (!isLighthouseBot) {
  useSyncAuthenticateDataTab()
  setMaintenanceGames()
  useLangdingPageLogin()
  useRegisterAddon()
}

if (isLogged.value) {
  initListAgency()
}
useHead({
  bodyAttrs: {
    class: computed(() => {
      const bodyClass = []

      if (store.items.length) {
        bodyClass.push('modal-open')
      }
      if (pageName.value) {
        bodyClass.push(`page-${pageName.value}`)
      }

      return bodyClass.join(' ')
    })
  }
})
onMounted(() => {
  if (!isLighthouseBot) {
    useSocket()
    if (sessionStorage.getItem('changePassword')) {
      sessionStorage.removeItem('changePassword')
    }

    openAuthModalByQuery()
    watch(
      () => currentUser.value.token,
      newToken => {
        if (!newToken) {
          resetCookie()
        }
      },
      { immediate: true }
    )
  }
})
const showHeroBanner = computed((): boolean => {
  return SHOW_HR_BANNER_URL.includes(route.path)
})

const isShowFooter = computed(() => {
  return route.path !== RESET_PASSWORD_URL
})
</script>
<template>
  <div
    class="layout-desktop position-relative"
    :class="[{ windows: isWindows }, { 'mac-os': isMacOS }, { chrome: isChrome }]"
  >
    <AppHeader />
    <main class="main-page">
      <template v-if="showHeroBanner">
        <ClientOnly>
          <BannerSlider />
        </ClientOnly>
      </template>
      <slot></slot>
    </main>
    <template v-if="!isLighthouseBot">
      <ModalLoginSignUp />
      <ModalForgotPassword />
      <ModalUpdateDisplayName />
      <LiveChat />
      <Modal500k />
      <ClientOnly>
        <template v-if="EVENT_PREMIER_LEAGUE_RANKING_ENABLED && !isHiddenEvent">
          <FloatingEvent />
          <WelcomePopup v-if="EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED" />
        </template>
        <template v-if="EVENT_HALLOWEEN_ENABLE && !isHiddenEventHalloween">
          <FloatingEventHalloween />
          <EventEntryHalloween v-if="EVENT_HALLOWEEN_POPUP_ENABLE" />
        </template>
      </ClientOnly>
    </template>
    <ModalResetPassword v-if="!isLighthouseBot" />
    <AppFooter v-if="!checkIsIframe && isShowFooter && !isLighthouseBot" />
  </div>
</template>
